import React, { useState, useEffect, useRef } from "react";
import "./TaskList.css";
import { TaskGroup, TaskItem } from "../../pages/Task/Task";
import { TaskService } from "../../services/task/taskService";
import ProductDetailsPopup from "../ProductDetailsPopup";
import { useToast } from "../common/ToastManager/ToastContext";

interface TaskHistoryTableProps {
  data: TaskGroup[];
  handleDownload: (taskId: string, isBulk: boolean) => void;
  refreshChatHistories: () => void;
  showProductNumber?: boolean;
  isLoading?: boolean;
}

const TaskList: React.FC<TaskHistoryTableProps> = ({
  data,
  handleDownload,
  refreshChatHistories,
  showProductNumber = false,
  isLoading = false,
}) => {
  const [expandedTasks, setExpandedTasks] = useState<number[]>([]);
  const [viewedTask, setViewedTask] = useState<TaskItem | null>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<string | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  const toggleMenu = (key: string) => {
    setOpenMenuIndex((prev) => (prev === key ? null : key));
  };

  const closeMenu = () => setOpenMenuIndex(null);

  const toggleTask = (taskId: number) => {
    setExpandedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // optional: 24-hour format
    });

  const handleStatusChange = async (
    taskId: number,
    tasknumber: string,
    newStatus: string
  ) => {
    try {
      const response = await TaskService.updateTaskStatus(taskId, newStatus);
      if (response.status !== 200) {
        showToast(
          `❌ Failed to update ${tasknumber.toUpperCase()} to '${newStatus}'`,
          "error"
        );
      }
    } catch (err) {
      showToast(
        `❌ Failed to update ${tasknumber.toUpperCase()} to '${newStatus}'`,
        "error"
      );
    } finally {
      refreshChatHistories();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (popupRef.current && !popupRef.current.contains(target)) {
        setViewedTask(null);
      }
      const dropdowns = document.querySelectorAll(".dropdown-open");
      const clickedInsideDropdown = Array.from(dropdowns).some((dropdown) =>
        dropdown.contains(target)
      );

      if (!clickedInsideDropdown) {
        setOpenMenuIndex(null);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setViewedTask(null);
        setOpenMenuIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const STATUS_LABELS: Record<string, string> = {
    pending: "Pending",
    queued: "Queued",
    processing: "Processing",
    completed: "Completed",
    failed: "Failed",
    cancelled: "Cancelled",
    onhold: "On Hold",
    partial: "Partial",
  };

  const renderActions = (task: TaskItem, key: string, isParent: boolean) => {
    if (
      task.status === "cancelled" ||
      task.status === "failed" ||
      task.status === "processing"
    )
      return null;
    return (
      <div className="action-wrapper" style={{ position: "relative" }}>
        <button className="download-button" onClick={() => toggleMenu(key)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="1.8"/>
            <circle cx="12" cy="12" r="1.8"/>
            <circle cx="19" cy="12" r="1.8"/>
          </svg>
        </button>
        {openMenuIndex === key && (
          <div className="task-dropdown-menu styled-dropdown dropdown-open">
            {task.status === "completed" && (
              <button
                className="task-dropdown-item"
                onClick={() => {
                  setViewedTask(task);
                  closeMenu();
                }}
              >
                <svg className="task-dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <span>View</span>
              </button>
            )}

            {task.status !== "cancelled" &&
              task.status !== "onhold" &&
              task.status !== "completed" && (
                <button
                  className="task-dropdown-item"
                  onClick={() => {
                    handleStatusChange(task.id, task.taskNumber, "onhold");
                    closeMenu();
                  }}
                >
                  <img src="/images/onhold.svg" alt="" />
                  <span>On Hold</span>
                </button>
              )}

            {task.status !== "cancelled" && task.status !== "completed" && (
              <button
                className="task-dropdown-item"
                onClick={() => {
                  handleStatusChange(task.id, task.taskNumber, "cancelled");
                  closeMenu();
                }}
              >
                <img src="/images/cancel.svg" alt="" />
                <span>Cancel</span>
              </button>
            )}

            {task.status === "onhold" && (
              <button
                className="task-dropdown-item"
                onClick={() => {
                  handleStatusChange(task.id, task.taskNumber, "resume");
                  closeMenu();
                }}
              >
                <img src="/images/resume.svg" alt="" />
                <span>Resume</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="task-container">
      <div className="task-table-wrapper">
        <table className="task-table">
          <thead>
            <tr>
              <th className="c1"></th>
              <th className="c2">TASK NUMBER</th>
              <th className="c3">NAME</th>
              {showProductNumber && <th className="c-product-num">PRODUCT NUMBER</th>}
              <th className="c4">TYPE</th>
              <th className="c5">COUNT</th>
              <th className="c6">SCHEDULED FOR</th>
              <th className="c7">STATUS</th>
              <th className="c8">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={showProductNumber ? 9 : 8} className="tbody-loader-cell">
                  <div className="tbody-spinner" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={showProductNumber ? 9 : 8}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  No tasks available.
                </td>
              </tr>
            ) : (
              data.map((group, index) => {
                const parent = group.parentTask;
                const isExpanded = expandedTasks.includes(parent.id);
                const hasChildren = group.children.length > 0;
                // Check if parent and all children are completed
                const allCompleted = group.children.every(
                  (child) => child.status === "completed"
                );
                return (
                  <React.Fragment key={parent.id}>
                    <tr className={hasChildren ? "file-name-row" : ""}>
                      <td className="c1">
                        {hasChildren ? (
                          <button
                            className="expand-button"
                            onClick={() => toggleTask(parent.id)}
                          >
                            {isExpanded ? (
                              <img src="/images/uparrow.svg" alt="Collapse" />
                            ) : (
                              <img src="/images/downarrow.svg" alt="Expand" />
                            )}
                          </button>
                        ) : (
                          ":::" // placeholder
                        )}
                      </td>
                      <td className="c2">{parent.taskNumber}</td>
                      <td className="c3">{parent.productTitle}</td>
                      {showProductNumber && (
                        <td className="c-product-num">
                          {parent.optimizely?.productNumber || "N/A"}
                        </td>
                      )}
                      <td className="c4">{parent.taskType}</td>
                      <td className="c5">{group.children.length || 1}</td>
                      <td className="c6">
                        {formatDate(parent.scheduledFor || parent.createdAt)},{" "}
                        {formatTime(parent.scheduledFor || parent.createdAt)}
                      </td>
                      <td className="c7">
                        <span
                          className={`status-badge ${parent.status.toLowerCase()}`}
                        >
                          {STATUS_LABELS[parent.status.toLowerCase()]}
                        </span>
                      </td>
                      <td className="c8">
                        {parent.status.toLowerCase() === "completed" &&
                        hasChildren ? (
                          <button
                            className="download-button"
                            onClick={() =>
                              handleDownload(String(parent.id), true)
                            }
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="7 10 12 15 17 10"/>
                              <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                          </button>
                        ) : (
                          renderActions(parent, `parent-${parent.id}`, true)
                        )}
                      </td>
                    </tr>

                    {isExpanded &&
                      group.children.map((child) => (
                        <tr key={child.id} className="expanded-row">
                          <td className="c1"></td>
                          <td className="c2">{child.taskNumber}</td>
                          <td className="c3">{child.productTitle}</td>
                          {showProductNumber && (
                            <td className="c-product-num">
                              {child.optimizely?.productNumber || "—"}
                            </td>
                          )}
                          <td className="c4">{child.taskType}</td>
                          <td className="c5">1</td>
                          <td className="c6">
                            {formatDate(child.scheduledFor || child.createdAt)},{" "}
                            {formatTime(child.scheduledFor || child.createdAt)}
                          </td>
                          <td className="c7">
                            <span className={`status-badge ${child.status}`}>
                              {STATUS_LABELS[child.status.toLowerCase()]}
                            </span>
                          </td>
                          <td className="c8">
                            {renderActions(child, `child-${child.id}`, false)}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Popup for viewing task response */}
      {viewedTask && (
        <ProductDetailsPopup
          taskItem={viewedTask}
          onClose={() => setViewedTask(null)}
        />
      )}
    </div>
  );
};

export default TaskList;
